//
// Copyright (c) 2011-2020 by Curalate, Inc.
//

import * as Css from 'csstype';
import React from 'react';
import styled, { css, keyframes } from 'styled-components';

const BACKGROUND_SIZE = 1000;
const BACKGROUND_COLOR: Css.Property.Color = '#f7f7f7';
const SHIMMER_COLOR: Css.Property.Color = '#f0f0f0';

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  width: Css.Property.Width;
  height: Css.Property.Height;
}

/**
 * The LoadingShimmer atom renders an element with an animated
 * "shimmer" effect background. The element can be set to any
 * size to act as a skeleton for elements as they're loading.
 */
export const LoadingShimmer = ({
  className,
  height,
  width,
  ...props
}: Props) => {
  return (
    <LoadingBar className={className} height={height} width={width} {...props}>
      {props.children}
    </LoadingBar>
  );
};

const loadingShine = keyframes`
  0% { background-position: -${BACKGROUND_SIZE / 2}px 0; }
  100% { background-position: ${BACKGROUND_SIZE / 2}px 0; }
`;

const LoadingBar = styled.div<Props>(
  ({ height, width }) => css`
    display: block;
    overflow: hidden;
    height: ${height};
    width: ${width};
    animation-duration: 1.5s;
    animation-fill-mode: forwards;
    animation-iteration-count: infinite;
    animation-timing-function: linear;
    animation-name: ${loadingShine};
    background: linear-gradient(
      to right,
      ${BACKGROUND_COLOR} 8%,
      ${SHIMMER_COLOR} 38%,
      ${BACKGROUND_COLOR} 54%
    );
    background-size: ${BACKGROUND_SIZE}px 50px;
  `,
);

export default LoadingShimmer;
